import { useCallback, useEffect, useState } from "react";
import {
  Waypoints, Loader2, KeyRound, Plus, Copy, Check, ShieldAlert, Fingerprint,
  ScrollText, TriangleAlert, ArrowRight, Ban, RotateCcw,
} from "lucide-react";
import { Panel, Pill } from "./ui";
import { nexsysApi } from "./api";
import { useNexsysGate } from "./gate";
import { EscalationQueue } from "./EscalationQueue";
import { IdentityBindings } from "./IdentityBindings";

const fmt = (t) => (t || "").slice(0, 16).replace("T", " ");

function KV({ k, v, tone }) {
  return (
    <div className="flex items-baseline gap-3">
      <span className="w-32 shrink-0 text-white/35">{k}</span>
      <span className={`min-w-0 truncate ${tone || "text-white/80"}`}>{v ?? "—"}</span>
    </div>
  );
}

function InteractionStory({ a }) {
  if (!a) return null;
  const conflict = (a.reconciliation || []).find((r) => r.classification === "CONFLICTING");
  const fresh = Object.entries(a.state_context?.freshness || {}).map(([k, v]) => `${v} ${k}`).join(", ");
  return (
    <Panel title="Recent Interaction" subtitle="Model-independent continuity — inspect exactly what the external interaction did" testid="gateway-story-panel">
      <div className="grid gap-x-10 gap-y-4 p-5 font-mono text-[11px] lg:grid-cols-3">
        <div className="space-y-1.5">
          <div className="text-[9.5px] tracking-[0.25em] text-cyan-300/60">ORIGIN</div>
          <KV k="Interface" v={a.via === "direct" ? "Direct (authenticated session)" : a.via} />
          <KV k="Principal" v={`user:${a.who}`} />
          <KV k="External identity" v={a.external_identity} />
          <KV k="Entity" v={`${a.entity?.entity_type || "user"}:${(a.entity?.entity_id || "").split("@")[0]}`} />
          <KV k="Interaction" v={a.interaction_id} />
          <KV k="Timestamp" v={fmt(a.at)} />
        </div>
        <div className="space-y-1.5">
          <div className="text-[9.5px] tracking-[0.25em] text-cyan-300/60">CONTINUUM</div>
          <KV k="CSO" v={(a.state_context?.cso_id || "").slice(0, 8)} />
          <KV k="State version" v={`v${a.state_context?.cso_version ?? "—"}`} />
          <KV k="Objects resumed" v={a.state_context?.objects_resumed} />
          <KV k="Freshness" v={fresh || "—"} tone="text-emerald-300/90" />
          <div className="pt-2 text-[9.5px] tracking-[0.25em] text-cyan-300/60">RECONCILIATION</div>
          {conflict ? (
            <>
              <KV k="External" v={conflict.content || "external assertion"} tone="text-rose-300/90" />
              <KV k="Result" v="CONFLICTING — governed state preserved" tone="text-rose-300" />
            </>
          ) : (
            <KV k="Result" v={(a.reconciliation || []).length ? (a.reconciliation || []).map((r) => r.classification).join(", ") : "no external context"} />
          )}
        </div>
        <div className="space-y-1.5">
          <div className="text-[9.5px] tracking-[0.25em] text-cyan-300/60">GOVERNANCE</div>
          <KV k="Decision" v={a.decision_id ? `DO-${a.decision_id.slice(0, 8)}` : "— (informational)"} />
          <KV k="Governance" v={a.authorized_action === false ? "NOT AUTHORIZED — proposed only" : a.message_type === "action_request" ? "authorized" : "n/a"} tone={a.authorized_action === false ? "text-amber-300" : undefined} />
          <KV k="Mediation" v={a.result_kind === "action_mediation" ? "action mediated" : "response mediated"} />
          <KV k="Disposition" v={(a.outcome || "").toUpperCase()} tone="text-cyan-300" />
          <KV k="Lineage" v={a.id?.slice(0, 8)} />
        </div>
      </div>
    </Panel>
  );
}

function StatTile({ label, value, testid }) {
  return (
    <div className="nx-panel rounded-xl px-4 py-3" data-testid={testid}>
      <div className="font-mono text-[10px] tracking-widest text-white/40">{label}</div>
      <div className="mt-1 font-display text-2xl font-semibold text-white">{value}</div>
    </div>
  );
}

export function GatewayConsole() {
  const { identity } = useNexsysGate();
  const isAdmin = !!identity?.is_admin;
  const [audit, setAudit] = useState(null);
  const [interfaces, setInterfaces] = useState([]);
  const [bindings, setBindings] = useState([]);
  const [err, setErr] = useState("");
  const [name, setName] = useState("");
  const [provider, setProvider] = useState("generic-api");
  const [providers, setProviders] = useState(["generic-api"]);
  const [creating, setCreating] = useState(false);
  const [newKey, setNewKey] = useState(null);
  const [copied, setCopied] = useState(false);
  const [busyIface, setBusyIface] = useState(null);
  const [selected, setSelected] = useState(null);
  const [reasons, setReasons] = useState({});
  const [busyConflict, setBusyConflict] = useState(null);
  const [resolveMsg, setResolveMsg] = useState(null);
  const [escalations, setEscalations] = useState(null);
  const [grants, setGrants] = useState([]);
  const [busyGrant, setBusyGrant] = useState(null);

  const load = useCallback(() => {
    Promise.all([nexsysApi.gatewayAudit(), nexsysApi.gatewayInterfaces(), nexsysApi.gatewayBindings(), nexsysApi.gatewayEscalations(), nexsysApi.oauthGrants()])
      .then(([a, i, b, e, g]) => {
        setAudit(a);
        setInterfaces(i.interfaces || []);
        setProviders(i.available_providers || ["generic-api"]);
        setBindings(b.bindings || []);
        setEscalations(e);
        setGrants(g.grants || []);
      })
      .catch(() => setErr("Gateway console requires admin authority"));
  }, []);

  const revokeGrant = async (id) => {
    setBusyGrant(id);
    try { await nexsysApi.oauthGrantRevoke(id); load(); }
    catch { /* admin only */ }
    finally { setBusyGrant(null); }
  };
  useEffect(() => { if (isAdmin) load(); }, [isAdmin, load]);

  const createInterface = async () => {
    if (!name.trim()) return;
    setCreating(true);
    try {
      const r = await nexsysApi.gatewayInterfaceCreate(name.trim(), provider);
      setNewKey(r);
      setName("");
      load();
    } catch { setErr("Interface registration failed"); }
    finally { setCreating(false); }
  };

  const setStatus = async (id, status) => {
    setBusyIface(id);
    try { await nexsysApi.gatewayInterfaceStatus(id, status); load(); }
    catch { /* admin only */ }
    finally { setBusyIface(null); }
  };

  const copyKey = () => {
    navigator.clipboard?.writeText(newKey.api_key);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  const resolveConflict = async (c, action) => {
    const reason = (reasons[c.id] || "").trim();
    if (action === "dismiss" && !reason) {
      setResolveMsg({ id: c.id, tone: "warn", text: "Dismissal requires a reason." });
      return;
    }
    setBusyConflict(c.id);
    setResolveMsg(null);
    try {
      const r = await nexsysApi.gatewayResolveConflict(c.id, action, reason, c.current_state_version);
      const out = r.resolution?.outcome;
      setResolveMsg({
        id: c.id, tone: "ok",
        text: out === "evidence-formalized"
          ? `Accepted as evidence → proposed Decision Object DO-${(r.resolution.decision_id || "").slice(0, 8)} (governed state unchanged)`
          : out === "escalated" ? "Escalated — conflict remains open for review"
          : out === "accepted-immaterial" ? "Accepted — below materiality threshold; recorded as evidence only"
          : "Dismissed with reason — audit history preserved",
      });
      load();
    } catch (e) {
      const msg = String(e?.message || "");
      setResolveMsg({
        id: c.id, tone: "warn",
        text: msg.includes("409")
          ? "Governed state changed since review — data refreshed, please reevaluate."
          : "Resolution failed.",
      });
      load();
    } finally { setBusyConflict(null); }
  };

  if (!isAdmin) {
    return (
      <div className="space-y-4">
        <IdentityBindings />
        <div className="flex items-center gap-3 rounded-xl border border-amber-400/20 bg-amber-400/[0.05] p-5 font-mono text-[12px] text-amber-200/80" data-testid="gateway-admin-only">
          <ShieldAlert className="h-4 w-4 shrink-0" />
          The AI Gateway console is restricted to admin authority. Interface registry, principal bindings and audit lineage are governance-controlled surfaces.
        </div>
      </div>
    );
  }
  if (err) return <div className="p-6 font-mono text-xs text-rose-300" data-testid="gateway-error">{err}</div>;
  if (!audit) return (
    <div className="flex items-center gap-2 p-6 font-mono text-xs text-white/40" data-testid="gateway-loading">
      <Loader2 className="h-4 w-4 animate-spin" /> Resolving Gateway control surface…
    </div>
  );

  const rows = audit.audit || [];
  const conflicts = audit.open_reconciliation || [];

  return (
    <div className="space-y-5" data-testid="gateway-console">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2.5">
          <Waypoints className="h-5 w-5 text-cyan-300" />
          <h1 className="font-display text-xl font-semibold tracking-tight text-white">AI Gateway</h1>
          <Pill tone="cyan" dot={false}>CONTROLLED ENTRY POINT · ENVELOPE v1.0</Pill>
        </div>
        <span className="font-mono text-[10px] text-white/35">External clients propose · EDIOS-governed state stays authoritative</span>
      </div>

      <div className="grid grid-cols-2 gap-3 lg:grid-cols-5">
        <StatTile label="MEDIATED INTERACTIONS" value={rows.length} testid="gateway-stat-interactions" />
        <StatTile label="APPROVED INTERFACES" value={interfaces.filter((i) => i.status === "active").length} testid="gateway-stat-interfaces" />
        <StatTile label="PRINCIPAL BINDINGS" value={bindings.length} testid="gateway-stat-bindings" />
        <StatTile label="OPEN CONFLICTS" value={conflicts.length} testid="gateway-stat-conflicts" />
        <StatTile label="ESCALATION QUEUE" value={escalations?.open_count ?? 0} testid="gateway-stat-escalations" />
      </div>

      <InteractionStory a={selected || rows[0]} />

      <div className="grid gap-4 xl:grid-cols-2">
        {/* interface registry */}
        <Panel title="Interface Registry" subtitle="Approved external AI interfaces — key shown once, revocable" testid="gateway-interfaces-panel">
          <div className="space-y-3 p-4">
            <div className="flex gap-2">
              <select
                value={provider}
                onChange={(e) => setProvider(e.target.value)}
                className="rounded-xl border border-white/10 bg-[#0a0f1a] px-2.5 py-2 font-mono text-[11px] text-cyan-200 outline-none focus:border-cyan-400/40"
                data-testid="gateway-provider-select"
              >
                {providers.map((p) => (
                  <option key={p} value={p}>{p === "openai" ? "OpenAI" : p === "claude" ? "Anthropic Claude" : "Generic API"}</option>
                ))}
              </select>
              <input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="New interface name (e.g. Partner Portal Bot)"
                className="flex-1 rounded-xl border border-white/10 bg-white/[0.03] px-3 py-2 text-[12px] text-white/85 outline-none placeholder:text-white/25 focus:border-cyan-400/40"
                data-testid="gateway-iface-name-input"
              />
              <button
                onClick={createInterface}
                disabled={creating || !name.trim()}
                className="flex items-center gap-1.5 rounded-xl border border-cyan-400/40 bg-cyan-400/10 px-3.5 py-2 text-[12px] font-medium text-cyan-300 hover:bg-cyan-400/20 disabled:opacity-40"
                data-testid="gateway-iface-create-btn"
              >
                {creating ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Plus className="h-3.5 w-3.5" />} Register
              </button>
            </div>
            {newKey && (
              <div className="rounded-xl border border-emerald-400/30 bg-emerald-400/[0.06] p-3" data-testid="gateway-new-key">
                <div className="font-mono text-[10px] tracking-widest text-emerald-300/80">API KEY — SHOWN ONLY ONCE</div>
                <div className="mt-1.5 flex items-center gap-2">
                  <code className="flex-1 truncate rounded bg-black/40 px-2 py-1 font-mono text-[11px] text-emerald-200">{newKey.api_key}</code>
                  <button onClick={copyKey} className="rounded-lg border border-white/10 p-1.5 text-white/60 hover:text-white" data-testid="gateway-copy-key-btn">
                    {copied ? <Check className="h-3.5 w-3.5 text-emerald-300" /> : <Copy className="h-3.5 w-3.5" />}
                  </button>
                </div>
              </div>
            )}
            <div className="nx-scroll max-h-[220px] space-y-2 overflow-y-auto">
              {interfaces.length === 0 && <div className="py-3 text-center font-mono text-[11px] text-white/35">No interfaces registered.</div>}
              {interfaces.map((i) => (
                <div key={i.id} className="flex items-center gap-3 rounded-lg border border-white/[0.06] bg-white/[0.02] px-3 py-2.5" data-testid="gateway-iface-row">
                  <KeyRound className={`h-3.5 w-3.5 shrink-0 ${i.status === "active" ? "text-cyan-300" : "text-white/25"}`} />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className={`h-1.5 w-1.5 rounded-full ${i.status === "active" ? "bg-emerald-400" : "bg-white/25"}`} />
                      <span className="truncate text-[12px] font-medium text-white/85">{i.name}</span>
                      <span className="font-mono text-[9px] uppercase tracking-widest text-cyan-300/70">{i.provider}</span>
                    </div>
                    <div className="font-mono text-[9.5px] text-white/35">
                      {(i.approval || "approved").toUpperCase()} · env {(i.envelope_versions || []).join(", ") || "—"} · model policy: {i.model_policy || "—"} · scope {i.scope || "—"}
                    </div>
                    <div className="font-mono text-[9.5px] text-white/30">
                      {i.auth_method} · {i.auth_status || "configured"} · {i.tool_actions || ""}
                    </div>
                    <div className="font-mono text-[9.5px] text-white/30">
                      last ok {i.last_activity ? fmt(i.last_activity) : "never"} · last fail {i.last_failed ? fmt(i.last_failed) : "none"} · health {i.health || "—"}
                    </div>
                  </div>
                  <Pill tone={i.status === "active" ? "emerald" : i.status === "disabled" ? "amber" : "rose"} dot={false}>{i.status.toUpperCase()}</Pill>
                  {i.status === "active" && (
                    <button
                      onClick={() => setStatus(i.id, "disabled")}
                      disabled={busyIface === i.id}
                      title="Disable (reversible)"
                      className="rounded-lg border border-white/10 p-1.5 text-white/50 hover:border-amber-400/40 hover:text-amber-300"
                      data-testid={`gateway-iface-disable-${i.id}`}
                    >
                      <Ban className="h-3.5 w-3.5" />
                    </button>
                  )}
                  <button
                    onClick={() => setStatus(i.id, i.status === "active" ? "revoked" : "active")}
                    disabled={busyIface === i.id}
                    title={i.status === "active" ? "Revoke" : "Re-activate"}
                    className="rounded-lg border border-white/10 p-1.5 text-white/50 hover:border-rose-400/40 hover:text-rose-300"
                    data-testid={`gateway-iface-toggle-${i.id}`}
                  >
                    {busyIface === i.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : i.status === "active" ? <ShieldAlert className="h-3.5 w-3.5" /> : <RotateCcw className="h-3.5 w-3.5" />}
                  </button>
                </div>
              ))}
            </div>
            <div className="rounded-xl border border-indigo-400/20 bg-indigo-400/[0.04] px-4 py-3" data-testid="gateway-mcp-info">
              <div className="font-mono text-[9.5px] tracking-[0.25em] text-indigo-300/70">NEXSYS MCP — PROVIDER-NEUTRAL CONNECTOR (CLAUDE · CHATGPT · ANY MCP CLIENT)</div>
              <code className="mt-1.5 block truncate rounded bg-black/40 px-2 py-1 font-mono text-[10.5px] text-indigo-200">
                {process.env.REACT_APP_BACKEND_URL}/api/mcp
              </code>
              <div className="mt-1.5 font-mono text-[9px] leading-relaxed text-white/40">
                One MCP server, one tool (<span className="text-indigo-300/80">nexsys_interact</span>), one Gateway contract.
                <span className="text-emerald-300/80"> OAuth sign-in supported</span>: add the URL in Claude (Settings → Connectors) or ChatGPT
                (Developer Mode → Connectors) and sign in with your NEXSYS account — no keys to paste. Interface keys remain a fallback
                (header <span className="text-white/60">Authorization: Bearer &lt;key&gt;</span> or <span className="text-white/60">x-api-key</span>).
                MCP is a transport only — every call flows through Gateway → Continuum → EDIOS governance.
              </div>
              {grants.length > 0 && (
                <div className="mt-2.5 space-y-1.5" data-testid="oauth-grants-list">
                  <div className="font-mono text-[9px] tracking-[0.25em] text-indigo-300/60">OAUTH GRANTS</div>
                  {grants.map((g) => (
                    <div key={g.id} className="flex items-center gap-2 rounded-lg border border-white/[0.06] bg-white/[0.02] px-2.5 py-1.5" data-testid="oauth-grant-row">
                      <span className={`h-1.5 w-1.5 shrink-0 rounded-full ${g.revoked ? "bg-rose-400/60" : "bg-emerald-400"}`} />
                      <div className="min-w-0 flex-1 font-mono text-[9.5px] text-white/60">
                        <span className="text-white/85">{g.client_name}</span> · {g.provider} · <span className="text-cyan-200/80">{g.email}</span>
                        <div className="text-[8.5px] text-white/30">
                          scope {g.scope} · issued {fmt(g.created_at)} · last used {g.last_used ? fmt(g.last_used) : "never"} · {g.rotations || 0} refresh{g.revoked ? ` · REVOKED (${g.revoked_reason || ""})` : ""}
                        </div>
                      </div>
                      {!g.revoked && (
                        <button
                          onClick={() => revokeGrant(g.id)}
                          disabled={busyGrant === g.id}
                          className="rounded-lg border border-white/10 px-2 py-1 font-mono text-[9px] text-white/50 hover:border-rose-400/40 hover:text-rose-300 disabled:opacity-40"
                          data-testid={`oauth-grant-revoke-${g.id}`}
                        >
                          {busyGrant === g.id ? "…" : "Revoke"}
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="rounded-xl border border-cyan-400/15 bg-cyan-400/[0.03] px-4 py-3 text-center font-mono text-[9.5px] leading-relaxed tracking-[0.2em] text-cyan-300/60" data-testid="gateway-shared-architecture">
              ALL PROVIDERS · SAME GATEWAY CONTRACT
              <span className="block text-white/25">↓</span>
              NEXSYS / CONTINUUM
              <span className="block text-white/25">↓</span>
              EDIOS GOVERNED STATE — ONE ENTERPRISE STATE, NO PER-PROVIDER COPIES
            </div>
          </div>
        </Panel>

        {/* principal bindings */}
        <Panel title="Principal Bindings" subtitle="External identity ↔ governed NEXSYS principal — never auto-equal" testid="gateway-bindings-panel">
          <div className="nx-scroll max-h-[330px] space-y-2 overflow-y-auto p-4">
            {bindings.length === 0 && <div className="py-3 text-center font-mono text-[11px] text-white/35">No bindings yet.</div>}
            {bindings.map((b) => (
              <div key={b.id} className="flex items-center gap-3 rounded-lg border border-white/[0.06] bg-white/[0.02] px-3 py-2.5" data-testid="gateway-binding-row">
                <Fingerprint className="h-3.5 w-3.5 shrink-0 text-indigo-300" />
                <div className="min-w-0 flex-1 font-mono text-[10.5px] text-white/60">
                  <span className="text-white/85">{b.external_identity}</span>
                  <ArrowRight className="mx-1.5 inline h-3 w-3 text-cyan-300" />
                  <span className="text-cyan-200">{b.email}</span>
                  <div className="text-[9.5px] text-white/30">
                    {b.provider} · bound {fmt(b.bound_at)} · last validated {b.last_validated ? fmt(b.last_validated) : "never"} · {b.authorization}
                  </div>
                </div>
                <Pill tone={b.status === "active" ? "emerald" : "rose"} dot={false}>{(b.status || "").toUpperCase()}</Pill>
              </div>
            ))}
          </div>
        </Panel>
      </div>

      <div className="grid gap-4 xl:grid-cols-[1.6fr_1fr]">
        {/* audit lineage */}
        <Panel title="Audit Lineage" subtitle="Every mediated interaction — who, via, state context, reconciliation" testid="gateway-audit-panel">
          <div className="nx-scroll max-h-[360px] space-y-2 overflow-y-auto p-4">
            {rows.length === 0 && <div className="py-3 text-center font-mono text-[11px] text-white/35">No interactions yet.</div>}
            {rows.map((a) => (
              <button
                key={a.id}
                onClick={() => setSelected(a)}
                className={`block w-full rounded-lg border px-3 py-2.5 text-left transition-colors ${
                  (selected || rows[0])?.id === a.id ? "border-cyan-400/30 bg-cyan-400/[0.05]" : "border-white/[0.06] bg-white/[0.02] hover:border-white/15"
                }`}
                data-testid="gateway-audit-row"
              >
                <div className="flex flex-wrap items-center gap-2">
                  <ScrollText className="h-3.5 w-3.5 shrink-0 text-cyan-300/70" />
                  <span className="font-mono text-[11px] text-white/85">{a.interaction_id}</span>
                  <Pill tone={a.outcome === "mediated" ? "cyan" : "rose"} dot={false}>{(a.result_kind || a.outcome || "").toUpperCase()}</Pill>
                  {a.authorized_action === false && <Pill tone="amber" dot={false}>NOT AUTHORIZED</Pill>}
                  {(a.reconciliation || []).some((r) => r.classification === "CONFLICTING") && <Pill tone="rose" dot={false}>CONFLICT</Pill>}
                </div>
                <div className="mt-1 font-mono text-[9.5px] leading-relaxed text-white/35">
                  {a.who} · via {a.via || "direct"} · ext {a.external_identity} · CSO v{a.state_context?.cso_version} ·{" "}
                  {a.state_context?.objects_resumed} obj · {Object.entries(a.state_context?.freshness || {}).map(([k, v]) => `${v} ${k}`).join(", ")} · {fmt(a.at)}
                </div>
              </button>
            ))}
          </div>
        </Panel>

        {/* open conflicts */}
        <Panel title="Open Reconciliation Conflicts" subtitle="Observability only — resolution flows through the Conflict Resolution Workflow" testid="gateway-conflicts-panel">
          <div className="nx-scroll max-h-[360px] space-y-2 overflow-y-auto p-4">
            {conflicts.length === 0 && (
              <div className="py-3 text-center font-mono text-[11px] text-emerald-300/60" data-testid="gateway-no-conflicts">No open conflicts.</div>
            )}
            {conflicts.map((c) => (
              <div key={c.id} className="rounded-lg border border-rose-400/20 bg-rose-500/[0.05] px-3 py-2.5" data-testid="gateway-conflict-row">
                <div className="flex items-center gap-2">
                  <TriangleAlert className="h-3.5 w-3.5 shrink-0 text-rose-300" />
                  <span className="font-mono text-[10px] tracking-widest text-rose-300/80">{(c.kind || "").toUpperCase()}</span>
                  {c.cso_version != null && <span className="font-mono text-[9px] text-white/30">state v{c.cso_version}</span>}
                </div>
                <div className="mt-1.5 font-mono text-[10.5px] leading-relaxed">
                  <div><span className="text-white/35">External: </span><span className="text-rose-200/90">{c.item?.content || c.attempted?.title || c.decision_id}</span></div>
                  {(c.governed_context || []).length > 0 && (
                    <div className="mt-0.5"><span className="text-white/35">Governed: </span><span className="text-cyan-200/80">{c.governed_context[0]}</span></div>
                  )}
                </div>
                <div className="mt-1 font-mono text-[9.5px] text-white/35">
                  {c.reason || (c.current_status ? `state already ${c.current_status}` : "")} · src {c.item?.source || "—"} · {c.principal || ""} · {fmt(c.at)} ·{" "}
                  {c.status === "escalated"
                    ? <span className="text-fuchsia-300/80">ESCALATED — REQUIRES REVIEW</span>
                    : <span className="text-amber-300/70">OPEN</span>}
                  {c.current_state_version != null && <span> · reviewing state v{c.current_state_version}</span>}
                </div>
                <div className="mt-2 space-y-1.5">
                  <input
                    value={reasons[c.id] || ""}
                    onChange={(e) => setReasons((m) => ({ ...m, [c.id]: e.target.value }))}
                    placeholder="Resolution reason (required for dismissal)"
                    className="w-full rounded-lg border border-white/10 bg-white/[0.03] px-2.5 py-1.5 font-mono text-[10px] text-white/80 outline-none placeholder:text-white/25 focus:border-cyan-400/40"
                    data-testid={`conflict-reason-${c.id}`}
                  />
                  <div className="flex flex-wrap gap-1.5">
                    <button
                      onClick={() => resolveConflict(c, "accept_as_evidence")}
                      disabled={busyConflict === c.id}
                      className="flex items-center gap-1.5 rounded-lg border border-emerald-400/40 bg-emerald-400/10 px-2.5 py-1.5 font-mono text-[10px] text-emerald-300 hover:bg-emerald-400/20 disabled:opacity-40"
                      data-testid={`conflict-accept-${c.id}`}
                    >
                      {busyConflict === c.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <Check className="h-3 w-3" />}
                      Accept as evidence
                    </button>
                    <button
                      onClick={() => resolveConflict(c, "dismiss")}
                      disabled={busyConflict === c.id}
                      className="flex items-center gap-1.5 rounded-lg border border-white/15 px-2.5 py-1.5 font-mono text-[10px] text-white/60 hover:border-rose-400/40 hover:text-rose-300 disabled:opacity-40"
                      data-testid={`conflict-dismiss-${c.id}`}
                    >
                      <Ban className="h-3 w-3" /> Dismiss
                    </button>
                    {c.status !== "escalated" && (
                      <button
                        onClick={() => resolveConflict(c, "escalate")}
                        disabled={busyConflict === c.id}
                        className="flex items-center gap-1.5 rounded-lg border border-fuchsia-400/30 px-2.5 py-1.5 font-mono text-[10px] text-fuchsia-300/80 hover:bg-fuchsia-400/10 disabled:opacity-40"
                        data-testid={`conflict-escalate-${c.id}`}
                      >
                        <TriangleAlert className="h-3 w-3" /> Escalate
                      </button>
                    )}
                  </div>
                  {resolveMsg?.id === c.id && (
                    <div
                      className={`font-mono text-[9.5px] ${resolveMsg.tone === "ok" ? "text-emerald-300/90" : "text-amber-300/90"}`}
                      data-testid={`conflict-resolve-msg-${c.id}`}
                    >
                      {resolveMsg.text}
                    </div>
                  )}
                </div>
              </div>
            ))}
            {resolveMsg && !conflicts.some((c) => c.id === resolveMsg.id) && (
              <div className={`font-mono text-[10px] ${resolveMsg.tone === "ok" ? "text-emerald-300/90" : "text-amber-300/90"}`} data-testid="conflict-resolve-summary">
                {resolveMsg.text}
              </div>
            )}
          </div>
        </Panel>
      </div>

      <EscalationQueue data={escalations} onChanged={load} />

      <IdentityBindings />
    </div>
  );
}
